import { useState, useEffect, useRef, useCallback } from 'preact/hooks';

type CropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type ImageCropperProps = {
  imageElement: HTMLImageElement;
  containerElement: HTMLElement;
  onCropChange?: (cropArea: CropArea) => void;
};

type DragState = {
  isDragging: boolean;
  dragStart: { x: number; y: number };
  dragType: 'move' | 'resize' | null;
  resizeDirection: string;
  cursor: string;
};

function calculateNewCropArea(
  currentCrop: CropArea,
  dragState: DragState,
  delta: { x: number; y: number },
  imageBounds: { left: number; top: number; right: number; bottom: number },
): CropArea {
  const newArea = { ...currentCrop };
  const minDim = 32;

  // Apply movement or resizing based on the drag type
  if (dragState.dragType === 'move') {
    newArea.x += delta.x;
    newArea.y += delta.y;
  } else if (dragState.dragType === 'resize') {
    if (dragState.resizeDirection.includes('e')) {
      newArea.width += delta.x;
    }
    if (dragState.resizeDirection.includes('w')) {
      newArea.width -= delta.x;
      newArea.x += delta.x;
    }
    if (dragState.resizeDirection.includes('s')) {
      newArea.height += delta.y;
    }
    if (dragState.resizeDirection.includes('n')) {
      newArea.height -= delta.y;
      newArea.y += delta.y;
    }
  }

  // Enforce minimum dimensions
  if (newArea.width < minDim) {
    if (dragState.resizeDirection.includes('w')) {
      newArea.x -= minDim - newArea.width;
    }
    newArea.width = minDim;
  }
  if (newArea.height < minDim) {
    if (dragState.resizeDirection.includes('n')) {
      newArea.y -= minDim - newArea.height;
    }
    newArea.height = minDim;
  }

  // Constrain the crop area to the image boundaries
  if (newArea.x < imageBounds.left) {
    newArea.width -= imageBounds.left - newArea.x;
    newArea.x = imageBounds.left;
  }
  if (newArea.y < imageBounds.top) {
    newArea.height -= imageBounds.top - newArea.y;
    newArea.y = imageBounds.top;
  }
  if (newArea.x + newArea.width > imageBounds.right) {
    newArea.width = imageBounds.right - newArea.x;
  }
  if (newArea.y + newArea.height > imageBounds.bottom) {
    newArea.height = imageBounds.bottom - newArea.y;
  }

  return newArea;
}

export function ImageCropper({ imageElement, containerElement, onCropChange }: ImageCropperProps) {
  const [cropArea, setCropArea] = useState<CropArea>(() => {
    const imageRect = imageElement.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();

    const displayWidth = imageRect.width;
    const displayHeight = imageRect.height;
    const imageLeftInContainer = imageRect.left - containerRect.left;
    const imageTopInContainer = imageRect.top - containerRect.top;

    // Calculate the crop size to be 80% of the smaller image dimension.
    const cropWidth = displayWidth * 0.8;
    const cropHeight = displayHeight * 0.8;

    return {
      x: imageLeftInContainer + displayWidth / 2 - cropWidth / 2,
      y: imageTopInContainer + displayHeight / 2 - cropHeight / 2,
      width: cropWidth,
      height: cropHeight,
    };
  });

  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragStart: { x: 0, y: 0 },
    dragType: null,
    resizeDirection: '',
    cursor: 'move',
  });

  const cropperRef = useRef<HTMLDivElement>(null);

  const getDirection = useCallback((x: number, y: number, rect: DOMRect): string => {
    const edgeSize = 10;
    const isNearTop = y < edgeSize;
    const isNearBottom = y > rect.height - edgeSize;
    const isNearLeft = x < edgeSize;
    const isNearRight = x > rect.width - edgeSize;

    if (isNearTop && isNearLeft) {
      return 'nw';
    }
    if (isNearTop && isNearRight) {
      return 'ne';
    }
    if (isNearBottom && isNearLeft) {
      return 'sw';
    }
    if (isNearBottom && isNearRight) {
      return 'se';
    }
    if (isNearTop) {
      return 'n';
    }
    if (isNearBottom) {
      return 's';
    }
    if (isNearLeft) {
      return 'w';
    }
    if (isNearRight) {
      return 'e';
    }
    return 'move';
  }, []);

  const getCursorStyle = useCallback((direction: string): string => {
    const cursors: Record<string, string> = {
      nw: 'nw-resize',
      n: 'n-resize',
      ne: 'ne-resize',
      e: 'e-resize',
      se: 'se-resize',
      s: 's-resize',
      sw: 'sw-resize',
      w: 'w-resize',
      move: 'move',
    };
    return cursors[direction] || 'default';
  }, []);

  const normalizeEvent = useCallback((e: MouseEvent | TouchEvent) => {
    if ('touches' in e) {
      const touch = e.changedTouches && e.changedTouches[0];
      if (touch) {
        return { clientX: touch.clientX, clientY: touch.clientY };
      }
    }
    return { clientX: (e as MouseEvent).clientX, clientY: (e as MouseEvent).clientY };
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      if (!cropperRef.current) {
        return;
      }

      const rect = cropperRef.current.getBoundingClientRect();
      const { clientX, clientY } = normalizeEvent(e);
      const direction = getDirection(clientX - rect.left, clientY - rect.top, rect);

      setDragState((prev) => ({
        ...prev,
        isDragging: true,
        dragStart: { x: clientX, y: clientY },
        dragType: direction === 'move' ? 'move' : 'resize',
        resizeDirection: direction,
      }));
    },
    [normalizeEvent, getDirection],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!dragState.isDragging) {
        if (!cropperRef.current) {
          return;
        }
        const rect = cropperRef.current.getBoundingClientRect();
        const { clientX, clientY } = normalizeEvent(e);
        const direction = getDirection(clientX - rect.left, clientY - rect.top, rect);
        setDragState((prev) => ({ ...prev, cursor: getCursorStyle(direction) }));
        return;
      }

      const { clientX, clientY } = normalizeEvent(e);
      const deltaX = clientX - dragState.dragStart.x;
      const deltaY = clientY - dragState.dragStart.y;

      const imageRect = imageElement.getBoundingClientRect();
      const containerRect = containerElement.getBoundingClientRect();
      const imageBounds = {
        left: imageRect.left - containerRect.left,
        top: imageRect.top - containerRect.top,
        right: imageRect.left - containerRect.left + imageRect.width,
        bottom: imageRect.top - containerRect.top + imageRect.height,
      };

      const newCropArea = calculateNewCropArea(
        cropArea,
        dragState,
        { x: deltaX, y: deltaY },
        imageBounds,
      );

      setCropArea(newCropArea);
      setDragState((prev) => ({ ...prev, dragStart: { x: clientX, y: clientY } }));
    },
    [
      dragState,
      cropArea,
      imageElement,
      containerElement,
      normalizeEvent,
      getDirection,
      getCursorStyle,
    ],
  );

  const handleMouseUp = useCallback(() => {
    setDragState((prev) => ({ ...prev, isDragging: false, dragType: null, resizeDirection: '' }));
  }, []);

  const handleCropperMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState.isDragging && cropperRef.current) {
        const rect = cropperRef.current.getBoundingClientRect();
        const direction = getDirection(e.clientX - rect.left, e.clientY - rect.top, rect);
        setDragState((prev) => ({ ...prev, cursor: getCursorStyle(direction) }));
      }
    },
    [dragState.isDragging, getDirection, getCursorStyle],
  );

  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, [dragState.isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (onCropChange) {
      onCropChange(cropArea);
    }
  }, [cropArea, onCropChange]);

  return (
    <div
      ref={cropperRef}
      class="absolute border border-black shadow-[inset_0_0_1px_1px_white,_0_0_0_10000px_rgba(0,0,0,0.25)] z-10"
      style={{
        left: `${cropArea.x}px`,
        top: `${cropArea.y}px`,
        width: `${cropArea.width}px`,
        height: `${cropArea.height}px`,
        cursor: dragState.cursor,
        boxSizing: 'content-box',
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onMouseMove={handleCropperMouseMove}
    >
      {/* Resize Handles */}
      <div class="absolute -top-1.5 -left-1.5 w-3 h-3 border border-gray-900 bg-white cursor-nwse-resize" />
      <div class="absolute -top-1.5 -right-1.5 w-3 h-3 border border-gray-900 bg-white cursor-nesw-resize" />
      <div class="absolute -bottom-1.5 -left-1.5 w-3 h-3 border border-gray-900 bg-white cursor-nesw-resize" />
      <div class="absolute -bottom-1.5 -right-1.5 w-3 h-3 border border-gray-900 bg-white cursor-nwse-resize" />
      <div class="absolute top-1/2 -left-1.5 -translate-y-1/2 w-3 h-3 border border-gray-900 bg-white cursor-ew-resize" />
      <div class="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-3 border border-gray-900 bg-white cursor-ew-resize" />
      <div class="absolute left-1/2 -top-1.5 -translate-x-1/2 w-3 h-3 border border-gray-900 bg-white cursor-ns-resize" />
      <div class="absolute left-1/2 -bottom-1.5 -translate-x-1/2 w-3 h-3 border border-gray-900 bg-white cursor-ns-resize" />
    </div>
  );
}

export function applyCrop(
  sourceImage: HTMLImageElement,
  cropArea: CropArea,
  containerElement: HTMLElement,
  rotation: number,
  fileType = 'image/png',
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const imageRect = sourceImage.getBoundingClientRect();
    const containerRect = containerElement.getBoundingClientRect();

    const isRotated = rotation % 180 !== 0;
    const naturalWidth = isRotated ? sourceImage.naturalHeight : sourceImage.naturalWidth;
    const naturalHeight = isRotated ? sourceImage.naturalWidth : sourceImage.naturalHeight;

    const scaleX = naturalWidth / imageRect.width;
    const scaleY = naturalHeight / imageRect.height;

    // Convert crop area from container coordinates to image coordinates
    const cropAreaRelativeToContainer = {
      x: cropArea.x,
      y: cropArea.y,
      width: cropArea.width,
      height: cropArea.height,
    };

    // Convert to absolute coordinates
    const cropAbsoluteX = containerRect.left + cropAreaRelativeToContainer.x;
    const cropAbsoluteY = containerRect.top + cropAreaRelativeToContainer.y;

    // Convert to image-relative coordinates
    const cropLeft = Math.max(0, (cropAbsoluteX - imageRect.left) * scaleX);
    const cropTop = Math.max(0, (cropAbsoluteY - imageRect.top) * scaleY);
    const cropWidth = Math.min(naturalWidth - cropLeft, cropAreaRelativeToContainer.width * scaleX);
    const cropHeight = Math.min(
      naturalHeight - cropTop,
      cropAreaRelativeToContainer.height * scaleY,
    );

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    if (rotation % 180 === 0) {
      canvas.width = cropWidth;
      canvas.height = cropHeight;
    } else {
      canvas.width = cropHeight;
      canvas.height = cropWidth;
    }

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);

    ctx.drawImage(
      sourceImage,
      cropLeft,
      cropTop,
      cropWidth,
      cropHeight,
      -cropWidth / 2,
      -cropHeight / 2,
      cropWidth,
      cropHeight,
    );

    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed.'))),
      fileType,
      0.92,
    );
  });
}
